import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  PageTabs,
  saveErrorToast,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetLineOfBusinessQuery } from "@generated";
import { LineOfBusinessForm } from "../LineOfBusinessForm/LineOfBusinessForm";
import { ServiceCategoriesEditor } from "../LineOfBusinessForm/ServiceCategoriesEditor/ServiceCategoriesEditor";
import {
  useLineOfBusinessMutations,
  useServiceCategoriesList,
  serviceApproverRowsFromIncluded,
  useServiceCategorySync,
} from "../api";
import { ApproversTab } from "../ApproversTab/ApproversTab";
import { emptyLobForm, LOB_EDIT_TABS } from "../types";
import type { LobEditTab, LobFormValues, ServiceCategoryRow } from "../types";
import "./Edit.css";

const FORM_ID = "lob-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const [activeTab, setActiveTab] = useState<LobEditTab>("lob");
  const { update } = useLineOfBusinessMutations();
  const { syncServiceCategories } = useServiceCategorySync();

  // The LOB GET folds in service approvers via `include` (service categories are
  // no longer includable, so they use their own list query below).
  const { data, isLoading, refetch } = useV2GetLineOfBusinessQuery(
    { id: id, include: ["service-approvers"] },
    { refetchOnMountOrArgChange: true }
  );

  const {
    rows: serverCategories,
    isFetching: catFetching,
    refetch: refetchCategories,
  } = useServiceCategoriesList(id);

  const approvers = useMemo(
    () => serviceApproverRowsFromIncluded(data?.included),
    [data]
  );

  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<LobFormValues>();
  const [submitting, setSubmitting] = useState(false);

  // Local, deferred service-category rows. Seed once from the server, then
  // reseed after each save (seededRef gates the effect; we reset it on save).
  const [categoryRows, setCategoryRows] = useState<ServiceCategoryRow[]>([]);
  const initialCategoriesRef = useRef<ServiceCategoryRow[]>([]);
  const seededRef = useRef(false);
  useEffect(() => {
    if (catFetching || seededRef.current) return;
    initialCategoriesRef.current = [...serverCategories];
    setCategoryRows(serverCategories);
    seededRef.current = true;
  }, [serverCategories, catFetching]);

  const loadedInitial = useMemo<LobFormValues>(() => {
    const attrs: any = data?.data?.attributes;
    if (!attrs) return emptyLobForm;
    return { name: attrs.name ?? "", code: attrs.code ?? "" };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: LobFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      await syncServiceCategories(
        id,
        initialCategoriesRef.current,
        categoryRows
      );
      markSaved(values, `'${values.name}' saved`);
      // No cache tags: reseed from server truth after save. refetch() refreshes
      // the LOB + included approvers; refetchCategories() the category list.
      seededRef.current = false;
      refetch();
      refetchCategories();
    } catch (err) {
      console.error("Failed to save line of business", err);
      setToast(saveErrorToast(err, "line of business"));
      // A mid-sequence failure may have partially applied the category sync.
      // Reseed from server truth so a retry doesn't re-create/re-delete
      // already-applied changes (unsaved local edits must be re-applied).
      seededRef.current = false;
      refetch();
      refetchCategories();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lob-edit-layout">
      <PageTabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key as LobEditTab)}
        tabs={LOB_EDIT_TABS}
      />
      {activeTab === "lob" && (
        <AdminFormPage
          title="Edit Line of Business"
          formId={FORM_ID}
          onCancel={() => navigate("../..", { relative: "path" })}
          isSubmitting={submitting}
        >
          <LineOfBusinessForm
            key={formKey}
            id={FORM_ID}
            initialValues={initialValues}
            isSubmitting={submitting}
            onSubmit={handleSubmit}
          />
          <hr />
          <ServiceCategoriesEditor
            rows={categoryRows}
            onChangeRows={setCategoryRows}
          />
        </AdminFormPage>
      )}
      {activeTab === "approvers" && (
        <ApproversTab lobId={id} approvers={approvers} onChanged={refetch} />
      )}
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Edit;
